import React from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, Share2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getBlogPostBySlug } from "@/data/blogPosts";
import NotFound from "@/pages/NotFound";

export const BlogPostPage: React.FC<{ onOpenConsultation?: () => void }> = ({
  onOpenConsultation,
}) => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;
  const [copied, setCopied] = React.useState(false);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: post?.title ?? "Feingold Property Management Article",
          url: window.location.href,
        })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!post) {
    return <NotFound />;
  }

  return (
    <article className="pt-28 pb-20 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Link */}
        <div className="mb-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Articles
          </Link>
        </div>

        {/* Header Block */}
        <header className="space-y-6 mb-12">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-extrabold uppercase tracking-widest text-accent bg-accent/10 px-3 py-1 rounded-md">
              {post.category}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {post.date}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {post.readTime}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15]">
            {post.title}
          </h1>

          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 font-normal leading-relaxed border-l-4 border-primary pl-4 py-1 italic">
            {post.excerpt}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm">
                {post.author.initials}
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {post.author.name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {post.author.location}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              className="text-xs gap-1.5"
              aria-label="Share article"
            >
              <Share2 className="w-3.5 h-3.5" />
              {copied ? "Link Copied!" : "Share"}
            </Button>
          </div>
        </header>

        {/* Main Content Body */}
        <div className="prose prose-slate dark:prose-invert max-w-none text-slate-700 dark:text-slate-300 space-y-8 leading-relaxed text-base sm:text-lg">
          {post.intro.map((para, i) => (
            <p key={i} className="m-0 text-slate-600 dark:text-slate-300">
              {para}
            </p>
          ))}

          {post.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <section
                key={idx}
                className="bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-6 sm:p-8 shadow-sm space-y-4 not-prose"
              >
                <div className="flex items-center gap-3">
                  {Icon ? (
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary font-extrabold flex items-center justify-center text-base">
                      {section.number}
                    </div>
                  )}
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white m-0">
                    {section.title}
                  </h2>
                </div>

                {section.paragraphs.map((para, i) => (
                  <p
                    key={i}
                    className={
                      i === 0 && section.paragraphs.length > 1
                        ? "m-0 font-semibold text-slate-900 dark:text-white"
                        : "m-0 text-slate-600 dark:text-slate-300"
                    }
                  >
                    {para}
                  </p>
                ))}

                {section.callout && (
                  <div
                    className={
                      section.callout.variant === "warning"
                        ? "p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-500 text-amber-900 dark:text-amber-200 font-semibold text-sm"
                        : "p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border-l-4 border-accent text-slate-800 dark:text-slate-200 font-medium text-sm"
                    }
                  >
                    {section.callout.text}
                  </div>
                )}
              </section>
            );
          })}

          {/* Conclusion */}
          <section className="bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-2xl p-8 sm:p-10 shadow-xl space-y-4 not-prose">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-accent text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Conclusion
            </div>

            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              {post.conclusion.title}
            </h2>

            {post.conclusion.paragraphs.map((para, i) => (
              <p
                key={i}
                className="text-slate-300 text-base leading-relaxed m-0"
              >
                {para}
              </p>
            ))}

            <div className="pt-2 text-accent font-semibold text-lg">
              {post.conclusion.highlight}
            </div>

            <div className="pt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onOpenConsultation}
                className="bg-accent hover:bg-accent/90 text-slate-950 font-bold px-6 py-3"
              >
                Schedule a Free Consultation
              </Button>
              <Link to="/rent-sell-calculator">
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto"
                >
                  Try the Rent vs Sell Calculator
                </Button>
              </Link>
            </div>
          </section>
        </div>

        {/* Related Navigation / Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> View All Articles
          </Link>
          <div className="flex gap-2">
            <Link to="/pricing">
              <Button variant="outline" size="sm">
                Explore Pricing
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="sm" className="bg-primary hover:bg-primary/90">
                Contact Feingold
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};
